/**
 * TikTok Pixel Standard Events Reference:
 * - ViewContent: Track product/content views
 * - AddToCart: Track when items added to cart
 * - InitiateCheckout: Track checkout initiations
 * - PlaceAnOrder: Track completed purchases
 * - Search: Track search queries
 */

/**
 * Safely track TikTok Pixel events with retry mechanism.
 * Ensures events are fired even if the Pixel script loads slightly after the component.
 */
export const trackTikTokEvent = (eventName: string, data?: Record<string, any>, options?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 3; // Quick check - if pixel isn't loaded after 300ms, it's not configured
  let attempts = 0;

  return new Promise((resolve) => {
    const track = () => {
      if (window.ttq) {
        // We use the event_id for deduplication matching the FB approach
        const eventId = options?.event_id || `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const eventOptions = { ...options, event_id: eventId };
        
        window.ttq.track(eventName, data, eventOptions);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        // TikTok Pixel not configured - silently resolve
        resolve();
      }
    };

    track();
  });
};

/**
 * Predefined event helpers for common e-commerce actions
 */
export const ttEvents = {
  viewContent: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
  }) => trackTikTokEvent('ViewContent', {
    content_id: product.id.toString(),
    content_type: 'product',
    contents: [{
      content_id: product.id.toString(),
      content_name: product.name,
      content_type: 'product',
      price: product.price,
      quantity: 1,
    }],
    value: product.price,
    currency: 'BDT'
  }),

  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
  }) => trackTikTokEvent('AddToCart', {
    content_id: product.id.toString(),
    content_type: 'product',
    contents: [{
      content_id: product.id.toString(),
      content_name: product.name,
      content_type: 'product',
      price: product.price,
      quantity: product.quantity || 1,
    }],
    value: product.price * (product.quantity || 1),
    currency: 'BDT'
  }),

  initiateCheckout: (items: Array<{
    id: string | number;
    quantity: number;
    price?: number;
  }>, totalValue: number) => trackTikTokEvent('InitiateCheckout', {
    content_id: items[0]?.id ? items[0].id.toString() : '',
    content_type: 'product',
    contents: items.map(item => ({
      content_id: item.id.toString(),
      content_type: 'product',
      quantity: item.quantity,
      price: item.price || 0,
    })),
    value: totalValue,
    currency: 'BDT'
  }),

  purchase: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number; price?: number }>;
    totalValue: number;
  }) => {
    const payload = {
      content_id: order.items[0]?.id ? order.items[0].id.toString() : '',
      content_type: 'product',
      contents: order.items.map(item => ({
        content_id: item.id.toString(),
        content_type: 'product',
        quantity: item.quantity,
        price: item.price || 0,
      })),
      value: order.totalValue,
      currency: 'BDT',
      order_id: order.orderId,
    };
    const eventOptions = { event_id: `Purchase_${order.orderId}` };
    // Track official standard 'Purchase' event
    trackTikTokEvent('Purchase', payload, eventOptions);
    // Also track legacy 'CompletePayment' for older ad campaigns
    trackTikTokEvent('CompletePayment', payload, { event_id: `Purchase_${order.orderId}_cp` });
  },

  placeAnOrder: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number; price?: number }>;
    totalValue: number;
  }) => {
    // Alias to purchase
    ttEvents.purchase(order);
  },

  search: (searchQuery: string) => trackTikTokEvent('Search', {
    query: searchQuery
  })
};
